import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShellAdmin, Button, ConvenioSwitcher, useThemeMode } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { AtlasBrand } from "../../components/AtlasBrand";
import { BotaoManual } from "../../components/BotaoManual";
import { LoadingOverlay, useTrocaOverlay } from "../../components/LoadingOverlay";
import {
  bancoEscopoNoModo,
  bancoPermissaoCobre,
  readBancoPermissoesFromJwt,
  readBancoSenhaTemporariaFromJwt,
} from "../../lib/banco-perms";
import { navDoBanco } from "./nav";

export function BancoLayout() {
  const nav = useNavigate();
  const location = useLocation();
  const { resolved, setMode } = useThemeMode();
  const qc = useQueryClient();

  const meQ = useQuery({ queryKey: ["banco", "me"], queryFn: () => atlas.banco.me(), staleTime: 60_000 });
  const modo = meQ.data?.modo_portal;
  // Convenio e' coisa de credito (#314): a consignataria so' de beneficios se
  // vincula ao orgao por rubrica, e o preset dela nem tem `convenios` — a
  // consulta so' devolveria 403. Espera o `me` para nao disparar a toa.
  const operaCredito = meQ.isSuccess && bancoEscopoNoModo("credito", modo);
  const convenios = useQuery({
    queryKey: ["banco", "convenios"],
    queryFn: () => atlas.banco.convenios(),
    enabled: operaCredito,
  });
  // Overlay reativo: fica visivel enquanto as queries ["banco"] estao em voo
  // (piso 800ms pra evitar flash, teto 5s como fail-safe). Fecha assim que
  // tudo carregou — nao mais o setTimeout fixo de 3s.
  const { troca, iniciar } = useTrocaOverlay();
  const setActive = useMutation({
    mutationFn: (id: string) => atlas.banco.setConvenioAtivo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banco"] });
    },
  });

  const trocarConvenio = (id: string) => {
    const alvo = convenios.data?.convenios.find((c) => c.id === id);
    if (!alvo || alvo.id === convenios.data?.activeId) return; // clique no proprio
    iniciar(alvo.nome, ["banco"]);
    setActive.mutate(id);
  };

  // activeKey ordinariamente e' o ultimo segmento do path. Excecao:
  // /banco/propostas tem submenu por produto controlado por query param
  // (?produto=emprestimo|cartao|portabilidade|cartao_beneficio) — usa o valor
  // pra destacar o filho certo no sidebar.
  const activeKey = ((): string => {
    const parts = location.pathname.split("/").filter(Boolean); // ["banco", "seg", "sub", ...]
    const seg = parts[1] ?? "visao-geral";
    const sub = parts[2];
    // Sub-rotas com parametro (:id, /novo, /:tipo) — mapeia pro item pai
    // certo do menu, senao activeKey vira o parametro e nada fica destacado.
    if (seg === "propostas") {
      // /propostas OU /propostas/:adf — ambos destacam pelo produto do query
      const p = new URLSearchParams(location.search).get("produto");
      if (p === "emprestimo" || p === "cartao" || p === "portabilidade" || p === "cartao_beneficio") return p;
      return "emprestimo";
    }
    if (seg === "cadastros") {
      // /cadastros/tabela-emprestimos[/novo|/:id] -> "tabela-emprestimos"
      // /cadastros/sub-organizacoes[/novo|/:id]   -> "sub-organizacoes"
      // /cadastros/usuarios[/novo|/:id]           -> "usuarios"
      if (sub === "tabela-emprestimos" || sub === "sub-organizacoes" || sub === "usuarios") return sub;
    }
    if (seg === "refinanciamentos") return "refinanciamento";
    if (seg === "margem-contratacao") return "margem-contratacao";
    if (seg === "relatorios" && sub) return sub; // consignacoes/gerador/faturamento
    return seg;
  })();

  // Permissoes granulares do OPERADOR logado (claim `banco_permissoes`).
  //
  // `undefined` = login-MASTER do banco, que ve tudo. Nao e' fallback
  // permissivo por descuido: e' o que impede o gating novo de esconder o menu
  // inteiro de todo banco ja cadastrado, cujo token em circulacao nao tem a
  // claim. O portao que de fato protege esta na API (`requireBancoPermissao`);
  // aqui e' so' para nao oferecer caminho que vai responder 403.
  const [permissoes, setPermissoes] = useState<string[] | undefined>(undefined);
  // `undefined` tambem e' "master": este flag separa "ainda nao li" de "li e
  // nao ha claim", para a contagem do menu nao disparar antes da leitura.
  const [permissoesLidas, setPermissoesLidas] = useState(false);
  useEffect(() => {
    setPermissoesLidas(true);
    try {
      const stored = window.localStorage.getItem("atlas:tokens");
      const token = stored ? JSON.parse(stored)?.access_token : null;
      setPermissoes(readBancoPermissoesFromJwt(token));
      // Senha temporaria: a API recusa TODAS as rotas de recurso com 403
      // `senha_temporaria_pendente`, entao sem este desvio o operador veria um
      // portal em que nada carrega. Quem barra e' o servidor; isto so' o leva
      // para a unica tela que resolve.
      if (readBancoSenhaTemporariaFromJwt(token) && !location.pathname.endsWith("/trocar-senha")) {
        nav("/banco/trocar-senha", { replace: true });
      }
    } catch {
      setPermissoes(undefined);
    }
  }, [location.pathname, nav]); // reavalia ao trocar de tela (token novo via refresh)

  // #318: pedidos de cancelamento esperando resposta. Nascem no app do servidor,
  // sem acao de ninguem aqui — por isso o polling, como o badge da averbadora.
  // So' consulta quem opera beneficios e pode ver a fila; senao seria 403.
  const podeVerPedidos =
    meQ.isSuccess && bancoEscopoNoModo("beneficios", modo) && ["solicitacoes", "beneficios"].some((r) => bancoPermissaoCobre(permissoes, r));
  const pedidosQ = useQuery({
    queryKey: ["banco", "pedidos-cancelamento", "contagem"],
    queryFn: () => atlas.banco.pedidosCancelamento.contagem(),
    enabled: permissoesLidas && podeVerPedidos,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const abertos = pedidosQ.data?.abertas;

  // Modo do credor + permissao do operador — ver `nav.ts`.
  const filteredNav = useMemo(
    () => navDoBanco(modo, permissoes, { "pedidos-cancelamento": abertos }),
    [modo, permissoes, abertos],
  );

  return (
    <AppShellAdmin
      brand={<AtlasBrand sub="Consignatária" />}
      sidebarFooter={<BotaoManual baixar={() => atlas.banco.manualBlob()} rotulo="Manual de uso" />}
      convenioSlot={
        operaCredito && convenios.data ? (
          <ConvenioSwitcher
            convenios={convenios.data.convenios}
            activeId={convenios.data.activeId}
            onChange={trocarConvenio}
          />
        ) : null
      }
      topbarSlot={
        <>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{location.pathname}</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {meQ.data ? (
              <span style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }} title="Consignatária logada">
                {meQ.data.nome}
              </span>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setMode(resolved === "dark" ? "light" : "dark")}>
              {resolved === "dark" ? "Tema claro" : "Tema escuro"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await atlas.logout().catch(() => undefined);
                window.localStorage.removeItem("atlas:role");
                window.localStorage.removeItem("atlas:tokens");
                nav("/login");
              }}
            >
              Sair
            </Button>
          </div>
        </>
      }
      nav={filteredNav}
      activeKey={activeKey}
      // URL corrente (com querystring). O AppShellAdmin usa isso pra
      // desambiguar os filhos de Propostas, que compartilham o mesmo path e
      // diferem pelo query param `produto`.
      activeHref={`${location.pathname}${location.search}`}
      onNavigate={(item) => {
        if (item.href) nav(item.href);
      }}
    >
      <Outlet />
      {troca ? <LoadingOverlay eyebrow="Trocando convênio" subtitulo={troca.subtitulo} /> : null}
    </AppShellAdmin>
  );
}
